import React from 'react';
import {Phone, MessageCircleDashed as MessageCircle, Wrench, Clock, MapPin, Star, Shield, CheckCircle} from 'lucide-react';

const Home = () => {
  const services = [
  {
    icon: <Wrench className="w-12 h-12" />,
    title: "General Repairs",
    description: "Expert diagnostics and repairs for all makes and models"
  },
  {
    icon: <Clock className="w-12 h-12" />,
    title: "Quick Service",
    description: "Fast, efficient service at your location"
  },
  {
    icon: <Shield className="w-12 h-12" />,
    title: "Quality Guaranteed",
    description: "All work backed by our quality guarantee"
  },
  {
    icon: <MapPin className="w-12 h-12" />,
    title: "Mobile Service",
    description: "We come to you - home, work, or roadside"
  }];


  const testimonials = [
  {
    name: "Sarah Johnson",
    rating: 5,
    text: "Absolutely brilliant service! My car broke down and they came to me within an hour. Fixed the problem quickly and professionally. Highly recommend!",
    location: "Manchester"
  },
  {
    name: "David Thompson",
    rating: 5,
    text: "Best mobile mechanic I've ever used. Honest, fair pricing and excellent work. Saved me the hassle of going to a garage. Will definitely use again.",
    location: "Birmingham"
  },
  {
    name: "Lisa Martinez",
    rating: 5,
    text: "Professional, knowledgeable and very friendly. Explained everything clearly and got my car running perfectly. Great value for money!",
    location: "Leeds"
  }];


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#333333] to-[#4682B4] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Professional Mobile Mechanics
                <span className="text-[#FFD700]"> At Your Doorstep</span>
              </h1>
              <p className="text-lg md:text-xl text-[#E0E0E0] leading-relaxed">
                Expert auto repair and maintenance services across Birmingham, Coventry, Wolverhampton and the West Midlands. Save time and hassle with ONPOINT Mobile Mechanics - your trusted mobile car repair specialists.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a
                  href="tel:07454922145"
                  className="bg-[#FFD700] text-[#333333] px-8 py-4 rounded-lg font-bold text-lg hover:bg-white transition-all flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
                  aria-label="Call ONPOINT Mobile Mechanics now">

                  <Phone className="w-5 h-5" />
                  <span>Call 07454 922145</span>
                </a>
                <a
                  href="https://wa.me/447308538351"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-[#333333] transition-all flex items-center justify-center space-x-2"
                  aria-label="Contact us on WhatsApp">

                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp Us</span>
                </a>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 pt-8">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-6 h-6 text-[#FFD700]" />
                  <span className="text-sm font-medium">Fully Qualified</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-6 h-6 text-[#FFD700]" />
                  <span className="text-sm font-medium">Insured</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-6 h-6 text-[#FFD700]" />
                  <span className="text-sm font-medium">Fast Response</span>
                </div>
              </div>
            </div>

            {/* Hero Image - Brand Logo */}
            <div className="relative flex items-center justify-center">
              <div className="relative p-8 bg-white rounded-2xl shadow-2xl">
                <img
                  src="https://static.lumi.new/material/62/62ec23acba7299fb23eb05933f999be0.png"
                  alt="ONPOINT Mobile Mechanics - Professional Mobile Auto Repair Service"
                  className="w-full max-w-md h-auto drop-shadow-2xl"></img>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Highlights */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-4">
              Why Choose ONPOINT?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional mobile mechanics delivering quality repairs and maintenance at your convenience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) =>
            <div
              key={index}
              className="bg-[#E0E0E0] p-6 rounded-lg hover:shadow-xl transition-all hover:-translate-y-2 group">

                <div className="text-[#4682B4] mb-4 group-hover:text-[#FFD700] transition-colors">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-[#333333] mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gradient-to-b from-[#E0E0E0] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-gray-600">
              Don't just take our word for it - hear from our satisfied customers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) =>
            <div
              key={index}
              className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all">

                {/* Star Rating */}
                <div className="flex space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) =>
                <Star key={i} className="w-5 h-5 fill-[#FFD700] text-[#FFD700]" />
                )}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{testimonial.text}"
                </p>

                {/* Customer Info */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-[#333333]">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-4">
              Mobile Mechanic Services Across the <span className="text-[#4682B4]">West Midlands</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Professional mobile mechanic services covering Birmingham, Coventry, Wolverhampton and surrounding areas. We come to your home, workplace or roadside location.
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#E0E0E0] to-white p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold text-[#333333] mb-6 text-center">
              Areas We Cover
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
              'Birmingham', 'Coventry', 'Wolverhampton', 'Dudley',
              'Walsall', 'Solihull', 'West Bromwich', 'Smethwick',
              'Oldbury', 'Tipton', 'Halesowen', 'Stourbridge',
              'Rowley Regis', 'Bilston', 'Willenhall', 'Sutton Coldfield',
              'Kingswinford', 'Brierley Hill', 'Aldridge', 'Brownhills',
              'Sedgley', 'Coseley'].
              map((area, index) =>
              <div
                key={index}
                className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl transition-all hover:-translate-y-1 border-l-4 border-[#4682B4]">

                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-[#4682B4] flex-shrink-0" />
                    <span className="font-semibold text-[#333333] text-sm">{area}</span>
                  </div>
                </div>
              )}
            </div>
            <p className="text-center text-gray-600 mt-8 text-lg">
              <strong>Don't see your area?</strong> Call us on <a href="tel:07454922145" className="text-[#4682B4] hover:text-[#FFD700] font-bold">07454 922145</a> - we may still be able to help!
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#4682B4] text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Your Car Serviced?
          </h2>
          <p className="text-xl mb-8 text-[#E0E0E0]">
            Call us now or send a WhatsApp message for fast, professional mobile mechanic services across Birmingham, Coventry, Wolverhampton and the West Midlands
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:07454922145"
              className="bg-[#FFD700] text-[#333333] px-8 py-4 rounded-lg font-bold text-lg hover:bg-white transition-all inline-flex items-center justify-center space-x-2 shadow-lg"
              aria-label="Call us now for mobile mechanic service">

              <Phone className="w-5 h-5" />
              <span>Call Now: 07454 922145</span>
            </a>
            <a
              href="https://wa.me/447308538351"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-[#333333] transition-all inline-flex items-center justify-center space-x-2"
              aria-label="Message us on WhatsApp">

              <MessageCircle className="w-5 h-5" />
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;